/**
 * Cliente HTTP do painel. Entende o `application/problem+json` da API e
 * transforma qualquer resposta de erro em `ApiError`, com o `correlationId` que
 * o suporte usa para achar o log.
 */

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly correlationId: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ProblemBody {
  title?: string;
  detail?: string;
  code?: string;
}

type TokenSource = () => string | null;
type UnauthorizedHandler = () => void;

let readToken: TokenSource = () => null;
let onUnauthorized: UnauthorizedHandler = () => undefined;

/** Ligado uma vez pelo provedor de sessão. */
export function configureApiClient(options: {
  token: TokenSource;
  unauthorized: UnauthorizedHandler;
}): void {
  readToken = options.token;
  onUnauthorized = options.unauthorized;
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = { Accept: 'application/json' };
  // Arquivo vai como multipart: o navegador escreve o Content-Type com o boundary.
  const isForm = body instanceof FormData;

  if (body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }

  if (token !== null) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    ...(body === undefined ? {} : { body: isForm ? body : JSON.stringify(body) }),
  });

  if (response.ok) {
    return (await response.json()) as T;
  }

  const problem = (await response.json().catch(() => ({}))) as ProblemBody;

  // Sessão vencida ou token recusado: volta para o login. O próprio login (sem
  // token) também recebe 401, e ali o erro precisa chegar à tela.
  if (response.status === 401 && token !== null) {
    onUnauthorized();
  }

  throw new ApiError(
    response.status,
    problem.code ?? null,
    problem.detail ?? problem.title ?? `Erro HTTP ${response.status}`,
    response.headers.get('X-Request-Id'),
  );
}

/**
 * Baixa um arquivo autenticado. Um `<a href>` não leva o token, então o arquivo
 * vem por `fetch` e é entregue ao navegador como blob.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const token = readToken();
  const response = await fetch(`${API_URL}${path}`, {
    headers: token === null ? {} : { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as ProblemBody;

    if (response.status === 401 && token !== null) {
      onUnauthorized();
    }

    throw new ApiError(
      response.status,
      problem.code ?? null,
      problem.detail ?? problem.title ?? `Erro HTTP ${response.status}`,
      response.headers.get('X-Request-Id'),
    );
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');

  link.href = url;
  link.download = name;
  link.click();
  // Revogar na hora pode cancelar o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** Monta a query string ignorando filtros vazios. */
export function toQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }

  const query = search.toString();

  return query.length > 0 ? `?${query}` : '';
}
